import React, { useState, useRef } from 'react';

const RetirementCalculatorSection = () => {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [monthlyExpenses, setMonthlyExpenses] = useState(50000);
  const [inflationRate, setInflationRate] = useState(6);
  const [currentMonthlySavings, setCurrentMonthlySavings] = useState(10000);
  const [existingSavings, setExistingSavings] = useState(500000);
  const [preRetirementReturn, setPreRetirementReturn] = useState(12);
  const [postRetirementReturn, setPostRetirementReturn] = useState(8);
  const [lifeExpectancy, setLifeExpectancy] = useState(80);
  const [result, setResult] = useState(null);
  
  const resultRef = useRef(null);

  const calculateRetirement = () => {
    const yearsToRetirement = retirementAge - currentAge;
    const yearsInRetirement = lifeExpectancy - retirementAge;
    
    // Calculate future monthly expenses at retirement (considering inflation)
    const futureMonthlyExpenses = monthlyExpenses * Math.pow(1 + inflationRate / 100, yearsToRetirement);
    
    // Calculate corpus needed at retirement
    const monthlyPostRetirementRate = postRetirementReturn / 100 / 12;
    const monthsInRetirement = yearsInRetirement * 12;
    const corpusNeeded = futureMonthlyExpenses * (1 - Math.pow(1 + monthlyPostRetirementRate, -monthsInRetirement)) / monthlyPostRetirementRate;
    
    // Calculate future value of existing savings
    const futureValueExistingSavings = existingSavings * Math.pow(1 + preRetirementReturn / 100, yearsToRetirement);
    
    // Calculate future value of current monthly savings
    const monthlyPreRetirementRate = preRetirementReturn / 100 / 12;
    const monthsToRetirement = yearsToRetirement * 12;
    const futureValueMonthlySavings = currentMonthlySavings * (Math.pow(1 + monthlyPreRetirementRate, monthsToRetirement) - 1) / monthlyPreRetirementRate;
    
    // Total corpus at retirement
    const totalCorpusAtRetirement = futureValueExistingSavings + futureValueMonthlySavings;
    
    // Calculate shortfall or surplus
    const shortfall = corpusNeeded - totalCorpusAtRetirement;
    
    // Calculate additional monthly SIP required if there's a shortfall
    let additionalMonthlySIP = 0;
    if (shortfall > 0) {
      additionalMonthlySIP = shortfall * monthlyPreRetirementRate / (Math.pow(1 + monthlyPreRetirementRate, monthsToRetirement) - 1);
    }

    // Calculate one-time additional saving required
    let oneTimeAdditionalSaving = 0;
    if (shortfall > 0) {
      oneTimeAdditionalSaving = shortfall / Math.pow(1 + preRetirementReturn / 100, yearsToRetirement);
    }
    
    const calculatedResult = {
      corpusNeeded: Math.round(corpusNeeded),
      futureValueExistingSavings: Math.round(futureValueExistingSavings),
      futureValueMonthlySavings: Math.round(futureValueMonthlySavings),
      totalCorpusAtRetirement: Math.round(totalCorpusAtRetirement),
      futureMonthlyExpenses: Math.round(futureMonthlyExpenses),
      shortfall: Math.round(shortfall),
      additionalMonthlySIP: Math.round(Math.max(0, additionalMonthlySIP)),
      oneTimeAdditionalSaving: Math.round(Math.max(0, oneTimeAdditionalSaving)),
      yearsToRetirement,
      yearsInRetirement,
      isSufficient: shortfall <= 0
    };
    
    setResult(calculatedResult);
    
    // Auto-scroll to result section after a short delay
    setTimeout(() => {
      if (resultRef.current) {
        resultRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  // Generate chart data points
  const generateChartData = () => {
    if (!result) return { currentInvestment: [], additionalInvestment: [] };
    
    const currentInvestmentData = [];
    const additionalInvestmentData = [];
    
    // Pre-retirement accumulation phase
    for (let age = currentAge; age <= retirementAge; age++) {
      const years = age - currentAge;
      const currentValue = existingSavings * Math.pow(1 + preRetirementReturn / 100, years) + 
                          currentMonthlySavings * ((Math.pow(1 + preRetirementReturn / 100 / 12, years * 12) - 1) / (preRetirementReturn / 100 / 12));
      
      const additionalValue = currentValue + 
                             (result.additionalMonthlySIP * ((Math.pow(1 + preRetirementReturn / 100 / 12, years * 12) - 1) / (preRetirementReturn / 100 / 12)));
      
      currentInvestmentData.push({ age, value: currentValue / 100000 }); // Convert to lakhs
      additionalInvestmentData.push({ age, value: additionalValue / 100000 });
    }
    
    // Post-retirement withdrawal phase
    let currentCorpus = result.totalCorpusAtRetirement;
    let additionalCorpus = result.totalCorpusAtRetirement + (result.shortfall > 0 ? result.shortfall : 0);
    
    for (let age = retirementAge + 1; age <= lifeExpectancy; age++) {
      // Reduce corpus by monthly expenses and apply post-retirement return
      currentCorpus = (currentCorpus - result.futureMonthlyExpenses * 12) * (1 + postRetirementReturn / 100);
      additionalCorpus = (additionalCorpus - result.futureMonthlyExpenses * 12) * (1 + postRetirementReturn / 100);
      
      currentInvestmentData.push({ age, value: Math.max(0, currentCorpus / 100000) });
      additionalInvestmentData.push({ age, value: Math.max(0, additionalCorpus / 100000) });
    }
    
    return { currentInvestment: currentInvestmentData, additionalInvestment: additionalInvestmentData };
  };

  const chartData = generateChartData();

  const styles = {
    retirementCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    // Result Section Styles
    resultSection: {
      marginTop: '3rem',
      padding: '0',
      backgroundColor: 'transparent'
    },
    resultHeader: {
      backgroundColor: '#c2e0f4',
      padding: '1rem 2rem',
      borderRadius: '8px 8px 0 0',
      border: '1px solid #a5c9dd',
      borderBottom: 'none'
    },
    resultHeaderText: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0'
    },
    resultTable: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '0 0 8px 8px',
      overflow: 'hidden',
      marginBottom: '2rem'
    },
    resultRow: {
      borderBottom: '1px solid #e5e7eb'
    },
    resultCell: {
      padding: '1rem 2rem',
      fontSize: '0.95rem',
      color: '#374151',
      backgroundColor: '#f9fafb'
    },
    resultValueCell: {
      padding: '1rem 2rem',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1f2937',
      backgroundColor: '#ffffff',
      textAlign: 'right'
    },
    greenText: {
      color: '#059669',
      fontWeight: '600'
    },
    redText: {
      color: '#dc2626',
      fontWeight: '600'
    },
    // Chart Styles
    chartSection: {
      marginTop: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '8px'
    },
    chartHeader: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#1f2937',
      textAlign: 'center',
      marginBottom: '1rem'
    },
    chartContainer: {
      width: '100%',
      height: '400px',
      position: 'relative',
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb'
    },
    svgChart: {
      width: '100%',
      height: '100%'
    },
    chartLegend: {
      display: 'flex',
      justifyContent: 'center',
      gap: '2rem',
      marginTop: '1rem'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem'
    },
    legendColor: {
      width: '20px',
      height: '3px',
      borderRadius: '2px'
    },
    recalculateButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #d1d5db',
      padding: '0.5rem 1.5rem',
      borderRadius: '6px',
      fontSize: '0.9rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginTop: '1rem'
    }
  };

  const renderChart = () => {
    if (!result || chartData.currentInvestment.length === 0) return null;

    const maxValue = Math.max(...chartData.additionalInvestment.map(d => d.value));
    const minAge = currentAge;
    const maxAge = lifeExpectancy;
    
    const chartWidth = 900;
    const chartHeight = 350;
    const padding = 60;
    
    const xScale = (age) => padding + ((age - minAge) / (maxAge - minAge)) * (chartWidth - 2 * padding);
    const yScale = (value) => chartHeight - padding - ((value / maxValue) * (chartHeight - 2 * padding));
    
    const currentPath = chartData.currentInvestment.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.age)} ${yScale(d.value)}`
    ).join(' ');
    
    const additionalPath = chartData.additionalInvestment.map((d, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(d.age)} ${yScale(d.value)}`
    ).join(' ');

    // Create filled areas
    const currentAreaPath = currentPath + ` L ${xScale(lifeExpectancy)} ${yScale(0)} L ${xScale(minAge)} ${yScale(0)} Z`;
    const additionalAreaPath = additionalPath + ` L ${xScale(lifeExpectancy)} ${yScale(0)} L ${xScale(minAge)} ${yScale(0)} Z`;

    return (
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={styles.svgChart}>
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
        
        {/* Filled areas */}
        <path d={currentAreaPath} fill="rgba(6, 182, 212, 0.3)" />
        <path d={additionalAreaPath} fill="rgba(251, 146, 60, 0.3)" />
        
        {/* Lines */}
        <path d={currentPath} stroke="#06b6d4" strokeWidth="3" fill="none" />
        <path d={additionalPath} stroke="#fb923c" strokeWidth="3" fill="none" />
        
        {/* Retirement age line */}
        <line 
          x1={xScale(retirementAge)} 
          y1={padding} 
          x2={xScale(retirementAge)} 
          y2={chartHeight - padding}
          stroke="#dc2626"
          strokeWidth="2"
          strokeDasharray="5,5"
        />
        <text 
          x={xScale(retirementAge)} 
          y={padding - 10} 
          textAnchor="middle" 
          fontSize="12" 
          fill="#dc2626"
          fontWeight="600"
        >
          Retirement Age
        </text>
        
        {/* Axes */}
        <line x1={padding} y1={chartHeight - padding} x2={chartWidth - padding} y2={chartHeight - padding} stroke="#374151" strokeWidth="2"/>
        <line x1={padding} y1={padding} x2={padding} y2={chartHeight - padding} stroke="#374151" strokeWidth="2"/>
        
        {/* Y-axis labels */}
        <text x={20} y={padding} textAnchor="middle" fontSize="12" fill="#374151">500</text>
        <text x={20} y={padding + (chartHeight - 2 * padding) * 0.2} textAnchor="middle" fontSize="12" fill="#374151">400</text>
        <text x={20} y={padding + (chartHeight - 2 * padding) * 0.4} textAnchor="middle" fontSize="12" fill="#374151">300</text>
        <text x={20} y={padding + (chartHeight - 2 * padding) * 0.6} textAnchor="middle" fontSize="12" fill="#374151">200</text>
        <text x={20} y={padding + (chartHeight - 2 * padding) * 0.8} textAnchor="middle" fontSize="12" fill="#374151">100</text>
        <text x={20} y={chartHeight - padding} textAnchor="middle" fontSize="12" fill="#374151">0</text>
        
        {/* X-axis labels */}
        {[31, 36, 41, 46, 51, 56, 61, 66, 71, 76].map(age => (
          <text key={age} x={xScale(age)} y={chartHeight - padding + 20} textAnchor="middle" fontSize="12" fill="#374151">
            {age}
          </text>
        ))}
        
        {/* Axis titles */}
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="14" fill="#374151" fontWeight="600">
          Age(Yrs)
        </text>
        <text x={25} y={chartHeight / 2} textAnchor="middle" fontSize="14" fill="#374151" fontWeight="600" transform={`rotate(-90, 25, ${chartHeight / 2})`}>
          Amount (Rs. in Lakhs)
        </text>
      </svg>
    );
  };

  return (
    <>
      <style>{`
        .retirement-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .retirement-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .retirement-calculate-btn:hover {
          background-color: #dc2626 !important;
        }
        
        .recalculate-btn:hover {
          background-color: #e5e7eb !important;
          border-color: #9ca3af !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .retirement-result-table {
            font-size: 0.85rem;
          }
          
          .retirement-result-cell,
          .retirement-result-value-cell {
            padding: 0.75rem 1rem !important;
          }
          
          .retirement-chart-container {
            height: 300px !important;
          }
        }
      `}</style>
      
      <div style={styles.retirementCalculatorContent}>
        <p style={styles.description}>
          It helps you to plan for saving sufficient amount to take care of your post retirement Household expenses.
        </p>
        
        {/* Your Current Age */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Your Current Age (Years)
            <input
              type="number"
              min="18"
              max="70"
              step="1"
              value={currentAge}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setCurrentAge('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setCurrentAge(Math.max(18, Math.min(70, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setCurrentAge(30);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter age"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="18"
              max="70"
              step="1"
              value={currentAge}
              onChange={(e) => setCurrentAge(parseInt(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>18</span>
              <span>23</span>
              <span>28</span>
              <span>34</span>
              <span>39</span>
              <span>44</span>
              <span>49</span>
              <span>54</span>
              <span>60</span>
              <span>65</span>
              <span>70</span>
            </div>
          </div>
        </div>

        {/* Your Retirement Age */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Your Retirement Age (Years)
            <input
              type="number"
              min="50"
              max="80"
              step="1"
              value={retirementAge}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setRetirementAge('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setRetirementAge(Math.max(50, Math.min(80, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setRetirementAge(60);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter age"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="50"
              max="80"
              step="1"
              value={retirementAge}
              onChange={(e) => setRetirementAge(parseInt(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>50</span>
              <span>53</span>
              <span>57</span>
              <span>62</span>
              <span>68</span>
              <span>73</span>
              <span>78</span>
              <span>80</span>
            </div>
          </div>
        </div>

        {/* Monthly Household Expenses */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Monthly household expenses for your current lifestyle (Rs.)
            <input
              type="number"
              min="1000"
              max="1000000"
              step="1000"
              value={monthlyExpenses}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setMonthlyExpenses('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setMonthlyExpenses(Math.max(1000, Math.min(1000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setMonthlyExpenses(50000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1000"
              max="1000000"
              step="1000"
              value={monthlyExpenses}
              onChange={(e) => setMonthlyExpenses(parseInt(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1,000</span>
              <span>250,750</span>
              <span>500,500</span>
              <span>750,250</span>
              <span>1,000,000</span>
            </div>
          </div>
        </div>

        {/* Estimated Rate of Inflation */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Estimated rate of Inflation (%)
            <input
              type="number"
              min="0"
              max="15"
              step="0.5"
              value={inflationRate}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setInflationRate('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setInflationRate(Math.max(0, Math.min(15, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setInflationRate(6);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="15"
              step="0.5"
              value={inflationRate}
              onChange={(e) => setInflationRate(parseFloat(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>1.5</span>
              <span>3</span>
              <span>4.5</span>
              <span>6</span>
              <span>7.5</span>
              <span>9</span>
              <span>10.5</span>
              <span>12</span>
              <span>13.5</span>
              <span>15</span>
            </div>
          </div>
        </div>

        {/* Your Current Monthly Savings */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Your current monthly savings (Rs.)
            <input
              type="number"
              min="0"
              max="2500000"
              step="1000"
              value={currentMonthlySavings}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setCurrentMonthlySavings('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setCurrentMonthlySavings(Math.max(0, Math.min(2500000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setCurrentMonthlySavings(10000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="2500000"
              step="1000"
              value={currentMonthlySavings}
              onChange={(e) => setCurrentMonthlySavings(parseInt(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>625,000</span>
              <span>1,250,000</span>
              <span>1,875,000</span>
              <span>2,500,000</span>
            </div>
          </div>
        </div>

        {/* How Much Already Saved */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            How much you have already saved for your retirement (Rs.)?
            <input
              type="number"
              min="0"
              max="100000000"
              step="100000"
              value={existingSavings}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExistingSavings('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setExistingSavings(Math.max(0, Math.min(100000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setExistingSavings(500000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="100000000"
              step="100000"
              value={existingSavings}
              onChange={(e) => setExistingSavings(parseInt(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>25,000,000</span>
              <span>50,000,000</span>
              <span>75,000,000</span>
              <span>100,000,000</span>
            </div>
          </div>
        </div>

        {/* Estimated Pre Retirement Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Estimated Pre Retirement Return (%)
            <input
              type="number"
              min="1"
              max="25"
              step="0.5"
              value={preRetirementReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setPreRetirementReturn('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setPreRetirementReturn(Math.max(1, Math.min(25, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setPreRetirementReturn(12);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="25"
              step="0.5"
              value={preRetirementReturn}
              onChange={(e) => setPreRetirementReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>3.4</span>
              <span>5.8</span>
              <span>8.2</span>
              <span>10.6</span>
              <span>13</span>
              <span>15.4</span>
              <span>17.8</span>
              <span>20.2</span>
              <span>22.6</span>
              <span>25</span>
            </div>
          </div>
        </div>

        {/* Estimated Post Retirement Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Estimated Post Retirement Return (%)
            <input
              type="number"
              min="1"
              max="20"
              step="0.5"
              value={postRetirementReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setPostRetirementReturn('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setPostRetirementReturn(Math.max(1, Math.min(20, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setPostRetirementReturn(8);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="20"
              step="0.5"
              value={postRetirementReturn}
              onChange={(e) => setPostRetirementReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>2.9</span>
              <span>4.8</span>
              <span>6.7</span>
              <span>8.6</span>
              <span>10.5</span>
              <span>12.4</span>
              <span>14.3</span>
              <span>16.2</span>
              <span>18.1</span>
              <span>20</span>
            </div>
          </div>
        </div>

        {/* Life Expectancy */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Life Expectancy
            <input
              type="number"
              min="70"
              max="100"
              step="1"
              value={lifeExpectancy}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setLifeExpectancy('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setLifeExpectancy(Math.max(70, Math.min(100, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setLifeExpectancy(80);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter age"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="70"
              max="100"
              step="1"
              value={lifeExpectancy}
              onChange={(e) => setLifeExpectancy(parseInt(e.target.value))}
              style={styles.slider}
              className="retirement-slider"
            />
            <div style={styles.sliderLabels}>
              <span>70</span>
              <span>73</span>
              <span>76</span>
              <span>79</span>
              <span>82</span>
              <span>85</span>
              <span>88</span>
              <span>91</span>
              <span>94</span>
              <span>97</span>
              <span>100</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="retirement-calculate-btn"
          onClick={calculateRetirement}
        >
          Calculate
        </button>

        {/* Results Section */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            <div style={styles.resultHeader}>
              <h3 style={styles.resultHeaderText}>Result</h3>
            </div>
            
            <table style={styles.resultTable} className="retirement-result-table">
              <tbody>
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Years left for retirement
                  </td>
                  <td style={styles.resultValueCell}>
                    {result.yearsToRetirement}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Your Monthly household expenses after retirement
                  </td>
                  <td style={styles.resultValueCell}>
                    {result.futureMonthlyExpenses.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Your targeted savings at retirement age {retirementAge}.0
                  </td>
                  <td style={styles.resultValueCell}>
                    {result.corpusNeeded.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Amount you will accumulate with current monthly savings
                  </td>
                  <td style={styles.resultValueCell}>
                    {result.futureValueMonthlySavings.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Your existing savings will grow to
                  </td>
                  <td style={styles.resultValueCell}>
                    {result.futureValueExistingSavings.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Total savings at the time of retirement
                  </td>
                  <td style={{...styles.resultValueCell, ...styles.greenText}}>
                    {result.totalCorpusAtRetirement.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Shortfall in savings
                  </td>
                  <td style={{...styles.resultValueCell, ...(result.shortfall > 0 ? styles.redText : styles.greenText)}}>
                    {result.shortfall > 0 ? result.shortfall.toLocaleString() : '0'}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Extra saving One time required to take care of shortfall
                  </td>
                  <td style={{...styles.resultValueCell, ...(result.oneTimeAdditionalSaving > 0 ? styles.redText : styles.greenText)}}>
                    {result.oneTimeAdditionalSaving > 0 ? result.oneTimeAdditionalSaving.toLocaleString() : '0'}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Extra saving per month required to take care of shortfall
                  </td>
                  <td style={{...styles.resultValueCell, ...(result.additionalMonthlySIP > 0 ? styles.redText : styles.greenText)}}>
                    {result.additionalMonthlySIP > 0 ? result.additionalMonthlySIP.toLocaleString() : '0'}
                  </td>
                </tr>
              </tbody>
            </table>
            
            {/* Retirement Chart */}
            <div style={styles.chartSection}>
              <h3 style={styles.chartHeader}>Retirement Chart</h3>
              <div style={styles.chartContainer} className="retirement-chart-container">
                {renderChart()}
              </div>
              <div style={styles.chartLegend}>
                <div style={styles.legendItem}>
                  <div style={{...styles.legendColor, backgroundColor: '#06b6d4'}}></div>
                  <span>With your Investment</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{...styles.legendColor, backgroundColor: '#fb923c'}}></div>
                  <span>With Additional Investment</span>
                </div>
              </div>
            </div>
            
            <button
              style={styles.recalculateButton}
              className="recalculate-btn"
              onClick={calculateRetirement}
            >
              Re-Calculate
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default RetirementCalculatorSection;